/**
 * NuclearnDataTable - the shared reusable data table for the app.
 *
 * Wraps Material React Table with the dataset-viewer feature set on by default
 * (sortable / resizable / pinnable / hideable columns, density toggle, global
 * filter). Generic over the row type. Supports both client- and server-side
 * pagination + sorting via the same prop shape MRT uses internally.
 *
 * The {@link tableTestId} prop drives a stable selector convention so callers
 * can keep their existing e2e selectors (e.g. `eai-dashboard-row-<id>`).
 */

import { useMemo } from "react";
import type { ReactNode } from "react";
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
  type MRT_PaginationState,
  type MRT_RowData,
  type MRT_SortingState,
  type MRT_TableOptions,
} from "material-react-table";
import { Alert, Box, Typography } from "@mui/material";

import { Loading } from "src/reusable/LoadingSpinner";

const sanitize = (value: string): string =>
  value.replace(/[^a-z0-9-]/gi, "-").toLowerCase();

type ColumnFiltersState = NonNullable<
  NonNullable<MRT_TableOptions<MRT_RowData>["state"]>["columnFilters"]
>;

export interface NuclearnDataTableProps<T extends MRT_RowData> {
  /** Column definitions. Memoize at the call site. */
  columns: MRT_ColumnDef<T>[];
  /** Row data. */
  data: T[];
  /** Stable row ID for selection / expansion / row testIds. */
  getRowId?: (row: T) => string;

  /** Total row count (server-side mode). Falls back to data.length. */
  rowCount?: number;

  isLoading?: boolean;
  isFetching?: boolean;
  /**
   * Empty + loading appearance. "body-spinner" (default) keeps the table chrome
   * (column headers) and renders a single centered spinner in the body where the
   * rows will land — preferred whenever the headers are known ahead of the fetch
   * (the usual case, since columns come from config/level). "spinner" replaces
   * the whole table with a centered spinner — only needed when the columns
   * themselves are derived from the not-yet-loaded data, so there are no headers
   * to show. "skeleton" keeps the chrome and renders MRT skeleton rows instead.
   */
  loadingVariant?: "spinner" | "skeleton" | "body-spinner";
  /** Renders a Material UI Alert instead of the table when set. */
  error?: Error | string | null;

  /** Controlled pagination (server-side). Omit for client-side. */
  pagination?: MRT_PaginationState;
  onPaginationChange?: MRT_TableOptions<T>["onPaginationChange"];

  /** Controlled sorting (server-side). Omit for client-side. */
  sorting?: MRT_SortingState;
  onSortingChange?: MRT_TableOptions<T>["onSortingChange"];

  /** Controlled column filters. Omit for uncontrolled / disabled. */
  columnFilters?: ColumnFiltersState;
  onColumnFiltersChange?: MRT_TableOptions<T>["onColumnFiltersChange"];

  /** Row click handler. */
  onRowClick?: (row: T) => void;

  /** Per-row extra HTML props (data-* attrs, sx overrides, etc.) — merged after the table's default row testid. */
  getRowProps?: (row: T) => Record<string, unknown>;

  /** Detail panel renderer (expandable rows). */
  renderDetailPanel?: MRT_TableOptions<T>["renderDetailPanel"];
  /** Gate which rows show the expand affordance (default: all rows expand). */
  getRowCanExpand?: MRT_TableOptions<T>["getRowCanExpand"];
  /** Row actions column renderer. Implies enableRowActions. */
  renderRowActions?: MRT_TableOptions<T>["renderRowActions"];
  /** Which end the actions column sits on. MRT's own default is "first". */
  positionActionsColumn?: MRT_TableOptions<T>["positionActionsColumn"];
  /** Content for the top toolbar's leading slot, beside the built-in search. */
  renderTopToolbarCustomActions?: MRT_TableOptions<T>["renderTopToolbarCustomActions"];

  /** Initial state (uncontrolled defaults: sorting, density, etc.). */
  initialState?: MRT_TableOptions<T>["initialState"];

  /**
   * Feature toggles. Defaults are minimal (sort + resize + bottom
   * pagination only). Anything richer — global filter, density toggle,
   * per-column ⋮ menu, hide-column UI, pinning — is opt-in so a default
   * table doesn't render with extra icons the caller didn't ask for.
   */
  enableColumnResizing?: boolean;
  enableColumnPinning?: boolean;
  enableHiding?: boolean;
  enableDensityToggle?: boolean;
  enableGlobalFilter?: boolean;
  enableColumnFilters?: boolean;
  enableColumnActions?: boolean;
  enableSorting?: boolean;
  enableTopToolbar?: boolean;
  enableBottomToolbar?: boolean;
  enablePagination?: boolean;
  enableStickyHeader?: boolean;

  /** Stable selector prefix for e2e tests. Required so every table is targetable. */
  tableTestId: string;

  /** Custom empty-state message. */
  emptyMessage?: string;
  /** Rich empty-state content (links, actions); wins over emptyMessage. */
  emptyContent?: ReactNode;

  /** Page size options for the bottom-toolbar selector. */
  pageSizeOptions?: number[];

  /** Container max-height (passed to muiTableContainerProps). */
  containerMaxHeight?: number | string;
  /** Drop the table's own card (shadow), for a table nested inside one. */
  flat?: boolean;
}

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const NuclearnDataTable = <T extends MRT_RowData>({
  columns,
  data,
  getRowId,
  rowCount,
  isLoading = false,
  isFetching = false,
  loadingVariant = "body-spinner",
  error = null,
  pagination,
  onPaginationChange,
  sorting,
  onSortingChange,
  columnFilters,
  onColumnFiltersChange,
  onRowClick,
  getRowProps,
  renderDetailPanel,
  getRowCanExpand,
  renderRowActions,
  positionActionsColumn,
  renderTopToolbarCustomActions,
  initialState,
  // Defaults: minimal chrome. Click a header to sort; drag the divider
  // to resize. Top toolbar (global filter, density toggle, hide menu)
  // and per-column ⋮ menus stay off so the common case looks clean.
  enableColumnResizing = true,
  enableColumnPinning = false,
  enableHiding = false,
  enableDensityToggle = false,
  enableGlobalFilter = false,
  enableColumnFilters = false,
  enableColumnActions = false,
  enableSorting = true,
  enableTopToolbar = false,
  enableBottomToolbar = true,
  enablePagination = true,
  enableStickyHeader = true,
  tableTestId,
  emptyMessage = "No records found",
  emptyContent,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  containerMaxHeight,
  flat = false,
}: NuclearnDataTableProps<T>) => {
  const manualPagination = pagination !== undefined;
  const manualSorting = sorting !== undefined;
  const manualFiltering = columnFilters !== undefined;

  // "body-spinner": keep the column headers but render one centered spinner in
  // the body (via renderEmptyRowsFallback) instead of MRT's skeleton rows.
  const showBodySpinner = isLoading && loadingVariant === "body-spinner";

  // Merge controlled state with the test-id-driven defaults.
  const state = useMemo(() => {
    const s: NonNullable<MRT_TableOptions<T>["state"]> = {
      // Suppress MRT's skeleton rows for the body-spinner variant so the empty
      // table renders our centered spinner fallback instead.
      isLoading: showBodySpinner ? false : isLoading,
      showProgressBars: isFetching && !isLoading,
    };
    if (pagination !== undefined) s.pagination = pagination;
    if (sorting !== undefined) s.sorting = sorting;
    if (columnFilters !== undefined) s.columnFilters = columnFilters;
    return s;
  }, [
    isLoading,
    isFetching,
    showBodySpinner,
    pagination,
    sorting,
    columnFilters,
  ]);

  const muiTableHeadCellProps: MRT_TableOptions<T>["muiTableHeadCellProps"] =
    useMemo(
      () =>
        ({ column }) =>
          ({
            "data-testid": `${tableTestId}-col-${sanitize(column.id)}`,
            sx: { fontWeight: 600 },
          }) as Record<string, unknown>,
      [tableTestId],
    );

  const muiTableBodyCellProps: MRT_TableOptions<T>["muiTableBodyCellProps"] =
    useMemo(
      () =>
        ({ column, row }) =>
          ({
            "data-testid": `${tableTestId}-cell-${sanitize(row.id)}-${sanitize(
              column.id,
            )}`,
          }) as Record<string, unknown>,
      [tableTestId],
    );

  const muiTableBodyRowProps: MRT_TableOptions<T>["muiTableBodyRowProps"] =
    useMemo(
      () =>
        ({ row, isDetailPanel }) => {
          const base: Record<string, unknown> = {
            "data-testid": `${tableTestId}-row-${sanitize(row.id)}`,
          };
          if (onRowClick) {
            base.onClick = () => onRowClick(row.original);
            base.sx = { cursor: "pointer" };
          }
          if (getRowProps && !isDetailPanel) {
            Object.assign(base, getRowProps(row.original));
          }
          return base;
        },
      [tableTestId, onRowClick, getRowProps],
    );

  const muiBottomToolbarProps = useMemo(
    () =>
      ({ "data-testid": `${tableTestId}-pagination` }) as Record<
        string,
        unknown
      >,
    [tableTestId],
  );

  const renderEmptyRowsFallback = useMemo(
    () => () =>
      showBodySpinner ? (
        <Box
          data-testid={`${tableTestId}-loading`}
          sx={{ display: "flex", justifyContent: "center", py: 6 }}
        >
          <Loading width="80px" />
        </Box>
      ) : (
        <Box
          data-testid={`${tableTestId}-empty`}
          sx={{ textAlign: "center", py: 6 }}
        >
          {emptyContent ?? (
            <Typography color="textSecondary">{emptyMessage}</Typography>
          )}
        </Box>
      ),
    [emptyMessage, emptyContent, tableTestId, showBodySpinner],
  );

  const muiPaginationProps = useMemo(
    () => ({
      rowsPerPageOptions: pageSizeOptions,
      showFirstButton: true,
      showLastButton: true,
    }),
    [pageSizeOptions],
  );

  const muiTableContainerProps = useMemo(
    () =>
      containerMaxHeight !== undefined
        ? { sx: { maxHeight: containerMaxHeight } }
        : undefined,
    [containerMaxHeight],
  );

  const table = useMaterialReactTable<T>({
    columns,
    data,
    ...(getRowId && { getRowId }),
    rowCount: rowCount ?? data.length,

    manualPagination,
    manualSorting,
    manualFiltering,
    ...(onPaginationChange && { onPaginationChange }),
    ...(onSortingChange && { onSortingChange }),
    ...(onColumnFiltersChange && { onColumnFiltersChange }),

    state,
    ...(initialState && { initialState }),

    enableColumnResizing,
    enableColumnPinning,
    enableHiding,
    enableDensityToggle,
    enableGlobalFilter,
    enableColumnFilters,
    enableColumnActions,
    enableSorting,
    enableStickyHeader,
    enableTopToolbar,
    enableBottomToolbar,
    enablePagination,
    enableFullScreenToggle: false,
    enableRowSelection: false,
    ...(renderDetailPanel && {
      enableExpanding: true,
      renderDetailPanel,
      ...(getRowCanExpand && { getRowCanExpand }),
      // MRT applies display:flex to the detail-panel cell in grid layout,
      // which makes the inner Collapse a flex item that shrinks to content
      // width. Switch back to display:block so the panel content (#19678)
      // fills the full row width instead of leaving a right-side gutter.
      muiDetailPanelProps: {
        sx: { display: "block", width: "100%" },
      },
    }),
    ...(renderRowActions && {
      enableRowActions: true,
      renderRowActions,
      ...(positionActionsColumn && { positionActionsColumn }),
    }),
    ...(renderTopToolbarCustomActions && { renderTopToolbarCustomActions }),

    muiTableHeadCellProps,
    muiTableBodyCellProps,
    muiTableBodyRowProps,
    muiBottomToolbarProps,
    muiPaginationProps,
    ...(muiTableContainerProps && { muiTableContainerProps }),

    renderEmptyRowsFallback,
    ...(flat && {
      muiTablePaperProps: {
        elevation: 0,
        sx: { border: 1, borderColor: "divider" },
      },
    }),
    layoutMode: "grid",
  });

  if (error) {
    return (
      <Alert
        severity="error"
        sx={{ m: 2 }}
        data-testid={`${tableTestId}-error`}
      >
        {typeof error === "string"
          ? error
          : `Failed to load: ${error.message ?? "unknown error"}`}
      </Alert>
    );
  }

  if (isLoading && data.length === 0 && loadingVariant === "spinner") {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 240,
        }}
        data-testid={`${tableTestId}-loading`}
      >
        <Loading width="80px" />
      </Box>
    );
  }

  return (
    <Box data-testid={tableTestId}>
      <MaterialReactTable table={table} />
    </Box>
  );
};

export default NuclearnDataTable;
